// The operations registry: the only place a path of the public API is written (spec/dashboard.md section 1.2).
// Each entry is typed against the generated `paths`, so an operation the OpenAPI golden does not document does not
// compile, and `contract.test.ts` checks the registry against the golden at test time as well.
import type { paths } from './schema';

export type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

/** The paths that define the method M. */
type PathsWith<M extends Method> = {
  [P in keyof paths]: [paths[P][M]] extends [never] ? never : undefined extends paths[P][M] ? never : P;
}[keyof paths];

export interface Operation<P extends keyof paths = keyof paths, M extends Method = Method> {
  readonly method: Uppercase<M>;
  readonly path: P;
  /** phantom: the lower-case method, for the type helpers */
  readonly m?: M;
}

export function op<M extends Method, P extends PathsWith<M>>(method: M, path: P): Operation<P, M> {
  return { method: method.toUpperCase() as Uppercase<M>, path } as Operation<P, M>;
}

type OpOf<O> = O extends Operation<infer P, infer M> ? NonNullable<paths[P][M]> : never;
type JsonOf<R> = R extends { content: { 'application/json': infer J } } ? J : undefined;
/** The JSON body of the operation's success response (200 / 201 / 202), or undefined for 204. */
export type ResponseOf<O> = OpOf<O> extends { responses: infer R }
  ? 200 extends keyof R ? JsonOf<R[200]> : 201 extends keyof R ? JsonOf<R[201]> : 202 extends keyof R ? JsonOf<R[202]> : undefined
  : never;
/**
 * The JSON request body of the operation (never when the operation takes none). The server's request records declare
 * every field, so the generated type marks them all required; the API treats an absent field as null, hence Partial.
 */
export type BodyOf<O> = OpOf<O> extends { requestBody: { content: { 'application/json': infer B } } }
  ? Partial<B>
  : OpOf<O> extends { requestBody?: { content: { 'application/json': infer B } } }
    ? Partial<B> | undefined
    : never;
/**
 * A declared query value with its wire spellings: a boolean also as a string (`'true'`, what the query string carries;
 * `src/content/useDocumentList.ts` builds `includeHeirs: 'true'` in an object whose type widens it to string); a closed
 * string set also as a plain string (`src/content/widgets/AssetPicker.tsx` passes an asset kind it holds as a string; the
 * server validates the value), keeping the declared names for completion. The parameter names stay exact.
 */
type WireValue<T> = T extends boolean ? T | 'true' | 'false' | (string & {}) : T extends string ? (string extends T ? T : T | (string & {})) : T;
/**
 * The query parameters of an operation as the OpenAPI document declares them (every query parameter is declared since M7
 * slice 1; the server answers 400 to an undeclared one), so a name the operation does not declare does not compile.
 * `null` is accepted for any of them: the client drops it.
 */
export type QueryOf<O> = OpOf<O> extends { parameters: { query?: infer Q } }
  ? [NonNullable<Q>] extends [never]
    ? never
    : { [K in keyof NonNullable<Q>]?: WireValue<NonNullable<Q>[K]> | null }
  : never;
type PathParamKeys<P> = P extends `${string}{${infer K}}${infer Rest}` ? K | PathParamKeys<Rest> : never;
/** The path parameters of an operation, by name. */
export type PathParamsOf<O> = O extends Operation<infer P, Method> ? Record<PathParamKeys<P>, string> : never;

/** Everything the dashboard calls, by operationId of the OpenAPI document. */
export const ops = {
  // auth and the caller
  signUp: op('post', '/v1/auth/sign-up'),
  signIn: op('post', '/v1/auth/sign-in'),
  signOut: op('post', '/v1/auth/sign-out'),
  getMe: op('get', '/v1/me'),
  listMyAudit: op('get', '/v1/me/audit'),
  getRoleCatalogue: op('get', '/v1/roles'),
  changePassword: op('post', '/v1/me:change-password'),
  listPersonalTokens: op('get', '/v1/me/tokens'),
  createPersonalToken: op('post', '/v1/me/tokens'),
  revokePersonalToken: op('post', '/v1/me/tokens/{token}:revoke'),
  // organizations and games
  listOrganizations: op('get', '/v1/orgs'),
  createOrganization: op('post', '/v1/orgs'),
  getOrganization: op('get', '/v1/orgs/{org}'),
  listGames: op('get', '/v1/orgs/{org}/games'),
  updateOrganization: op('patch', '/v1/orgs/{org}'),
  createGame: op('post', '/v1/orgs/{org}/games'),
  getGame: op('get', '/v1/orgs/{org}/games/{game}'),
  getGamePermissions: op('get', '/v1/orgs/{org}/games/{game}/permissions'),
  listEnvironments: op('get', '/v1/orgs/{org}/games/{game}/envs'),
  listBranches: op('get', '/v1/orgs/{org}/games/{game}/branches'),
  createBranch: op('post', '/v1/orgs/{org}/games/{game}/branches'),
  getBranch: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}'),
  updateBranch: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}'),
  listGameAudit: op('get', '/v1/orgs/{org}/games/{game}/audit'),
  getGameAuditEntry: op('get', '/v1/orgs/{org}/games/{game}/audit/{entry}'),
  getJob: op('get', '/v1/orgs/{org}/games/{game}/jobs/{job}'),
  listPins: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/pins'),
  getRelease: op('get', '/v1/orgs/{org}/games/{game}/releases/{release}'),
  // schema and documents
  listTemplates: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates'),
  getTemplate: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates/{template}'),
  listEnums: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/enums'),
  listDocuments: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents'),
  getDocument: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents/{document}'),
  createDocument: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents'),
  patchDocument: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents/{document}'),
  deleteDocument: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents/{document}'),
  listDocumentReferences: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents/{document}/references'),
  listDocumentHistory: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents/{document}/history'),
  batchDocuments: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents:batch'),
  // history
  listCommits: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/commits'),
  getCommit: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/commits/{sha}'),
  compareCommits: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/compare'),
  validateBranch: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}:validate'),
  // slice 2: schema and document editors
  listTags: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/tags'),
  changeDocumentTemplate: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/documents/{document}:change-template'),
  createEnum: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/enums'),
  getEnum: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/enums/{enum}'),
  updateEnum: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/enums/{enum}'),
  deleteEnum: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/enums/{enum}'),
  createTemplate: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates'),
  updateTemplate: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates/{template}'),
  deleteTemplate: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates/{template}'),
  addParameter: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates/{template}/params'),
  updateParameter: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates/{template}/params/{param}'),
  deleteParameter: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/templates/{template}/params/{param}'),
  getBranchSchema: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/schema'),
  listLocalizationStrings: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/strings'),
  createLocalizationString: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/strings'),
  getLocalizationString: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/strings/{string}'),
  batchLocalizationStrings: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/strings:batch'),
  listLocalizationGroups: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/groups'),
  getLocalizationSettings: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/settings'),
  listAssets: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets'),
  getAsset: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets/{asset}'),
  uploadAsset: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets:upload'),
  getAssetContent: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets/{asset}/content'),
  listProducts: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/products'),
  listReleases: op('get', '/v1/orgs/{org}/games/{game}/releases'),
  // slice 4 (spec/api-conventions.md section 20.3): the environment figures and the A/B winner commit
  getSegmentSizes: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/segments'),
  refreshSegmentSizes: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/segments:refresh'),
  getAbTestResults: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/ab-tests/{test}/results'),
  getRevenue: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/revenue'),
  applyAbTest: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/ab-tests/{test}:apply'),
  // slice 5 (spec/dashboard.md section 9): localization, assets, Addressables, products, platforms, environment settings
  createProduct: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/products'),
  updateProduct: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/products/{product}'),
  deleteProduct: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/products/{product}'),
  listProductReferences: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/products/{product}/references'),
  updateAsset: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets/{asset}'),
  deleteAsset: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets/{asset}'),
  restoreAsset: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets/{asset}:restore'),
  putAssetContent: op('put', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets/{asset}/content'),
  listAssetReferences: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets/{asset}/references'),
  listAddressables: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/addressables'),
  deleteAddressables: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/addressables/{platform}'),
  updateLocalizationSettings: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/settings'),
  renameLocalizationGroup: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/groups:rename'),
  deleteLocalizationString: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/strings/{string}'),
  updateLocalizationString: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/strings/{string}'),
  listLocalizationStringReferences: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/localization/strings/{string}/references'),
  importTable: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}:import'),
  exportTable: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}:export'),
  getEnvironment: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}'),
  updateEnvironment: op('patch', '/v1/orgs/{org}/games/{game}/envs/{env}'),
  listPlatformConfigs: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/platforms'),
  putPlatformConfig: op('put', '/v1/orgs/{org}/games/{game}/envs/{env}/platforms/{platform}'),
  deletePlatformConfig: op('delete', '/v1/orgs/{org}/games/{game}/envs/{env}/platforms/{platform}'),
  playerHistoryUsage: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/history/usage'),
  getEnvironmentStatus: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/status'),
  runPlayerHistoryRetention: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/players:retention'),
  // slice 7 (spec/api-conventions.md sections 16–17, 19.5; spec/dashboard.md section 11): players, their saves and history, orders
  findPlayers: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players'),
  recentPlayers: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/recent'),
  getPlayer: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}'),
  listPlayerCollections: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/saves'),
  getPlayerCollection: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/saves/{collection}'),
  editPlayerSave: op('put', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/saves/{collection}/{key}'),
  restorePlayerSaves: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/saves/{collection}:restore'),
  listPlayerHistory: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/history'),
  getPlayerVersion: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/history/{version}'),
  diffPlayerVersion: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/history/{version}/diff'),
  resetPlayer: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}:reset'),
  erasePlayer: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}:erase'),
  exportPlayer: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}:export'),
  getErasureReceipt: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/erasures/{receipt}'),
  listOrders: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/orders'),
  getOrder: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/orders/{order}'),
  commitOrder: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/orders/{order}:commit'),
  claimOrder: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/orders/{order}:claim'),
  failOrder: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/orders/{order}:fail'),
  expireOrders: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/orders:expire'),
  getReleaseSchema: op('get', '/v1/orgs/{org}/games/{game}/releases/{release}/schema'),
  // slice 8 (spec/dashboard.md section 12): history operations, merge requests, releases and pins, jobs, members, keys, audit, settings
  revertCommit: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/commits/{sha}:revert'),
  cherryPickCommit: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/commits/{sha}:cherry-pick'),
  deleteBranch: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}'),
  listMergeRequests: op('get', '/v1/orgs/{org}/games/{game}/merge-requests'),
  createMergeRequest: op('post', '/v1/orgs/{org}/games/{game}/merge-requests'),
  getMergeRequest: op('get', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}'),
  updateMergeRequest: op('patch', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}'),
  getMergeRequestDiff: op('get', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/diff'),
  getMergeRequestConflicts: op('get', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/conflicts'),
  resolveMergeRequestConflicts: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}:resolve'),
  approveMergeRequest: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}:approve'),
  unapproveMergeRequest: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}:unapprove'),
  mergeMergeRequest: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}:merge'),
  closeMergeRequest: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}:close'),
  reopenMergeRequest: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}:reopen'),
  listMergeRequestComments: op('get', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/comments'),
  createMergeRequestComment: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/comments'),
  updateMergeRequestComment: op('patch', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/comments/{comment}'),
  resolveMergeRequestComment: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/comments/{comment}:resolve'),
  unresolveMergeRequestComment: op('post', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/comments/{comment}:unresolve'),
  deleteMergeRequestComment: op('delete', '/v1/orgs/{org}/games/{game}/merge-requests/{mr}/comments/{comment}'),
  buildRelease: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/releases'),
  getReleaseManifest: op('get', '/v1/orgs/{org}/games/{game}/releases/{release}/manifest'),
  pinRelease: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/pins'),
  revokePin: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/pins/{pin}:revoke'),
  rollbackPin: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/pins:rollback'),
  getPointer: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/pointer'),
  resolvePointer: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/pointer:resolve'),
  listJobs: op('get', '/v1/orgs/{org}/games/{game}/jobs'),
  listOrganizationAudit: op('get', '/v1/orgs/{org}/audit'),
  getOrganizationAuditEntry: op('get', '/v1/orgs/{org}/audit/{entry}'),
  listOrganizationMembers: op('get', '/v1/orgs/{org}/members'),
  addOrganizationMember: op('post', '/v1/orgs/{org}/members'),
  updateOrganizationMember: op('patch', '/v1/orgs/{org}/members/{user}'),
  removeOrganizationMember: op('delete', '/v1/orgs/{org}/members/{user}'),
  updateGame: op('patch', '/v1/orgs/{org}/games/{game}'),
  archiveGame: op('delete', '/v1/orgs/{org}/games/{game}'),
  restoreGame: op('post', '/v1/orgs/{org}/games/{game}:restore'),
  rotateGamePublicKey: op('post', '/v1/orgs/{org}/games/{game}:rotate-public-key'),
  listGameMembers: op('get', '/v1/orgs/{org}/games/{game}/members'),
  addGameMember: op('post', '/v1/orgs/{org}/games/{game}/members'),
  updateGameMember: op('patch', '/v1/orgs/{org}/games/{game}/members/{user}'),
  removeGameMember: op('delete', '/v1/orgs/{org}/games/{game}/members/{user}'),
  listOrganizationServiceKeys: op('get', '/v1/orgs/{org}/keys'),
  createOrganizationServiceKey: op('post', '/v1/orgs/{org}/keys'),
  rotateOrganizationServiceKey: op('post', '/v1/orgs/{org}/keys/{key}:rotate'),
  revokeOrganizationServiceKey: op('post', '/v1/orgs/{org}/keys/{key}:revoke'),
  listGameServiceKeys: op('get', '/v1/orgs/{org}/games/{game}/keys'),
  createGameServiceKey: op('post', '/v1/orgs/{org}/games/{game}/keys'),
  rotateGameServiceKey: op('post', '/v1/orgs/{org}/games/{game}/keys/{key}:rotate'),
  revokeGameServiceKey: op('post', '/v1/orgs/{org}/games/{game}/keys/{key}:revoke'),
  // slice 6 (spec/api-conventions.md section 20.5): extensions as files of the branch
  listExtensions: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/extensions'),
  createExtension: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/extensions'),
  getExtension: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/extensions/{name}'),
  replaceExtensionSource: op('put', '/v1/orgs/{org}/games/{game}/branches/{branch}/extensions/{name}'),
  updateExtension: op('patch', '/v1/orgs/{org}/games/{game}/branches/{branch}/extensions/{name}'),
  deleteExtension: op('delete', '/v1/orgs/{org}/games/{game}/branches/{branch}/extensions/{name}'),
  listExtensionHistory: op('get', '/v1/orgs/{org}/games/{game}/branches/{branch}/extensions/{name}/history'),
  // M7 slice 3 (spec/api-conventions.md section 21.3): invites, password reset, integrations, the host switch, signed asset URLs, the ban
  getAuthCapabilities: op('get', '/v1/auth/capabilities'),
  requestPasswordReset: op('post', '/v1/auth/password-reset:request'),
  completePasswordReset: op('post', '/v1/auth/password-reset:complete'),
  inspectInvite: op('post', '/v1/auth/invites:inspect'),
  acceptInvite: op('post', '/v1/auth/invites:accept'),
  listInvites: op('get', '/v1/orgs/{org}/invites'),
  createInvite: op('post', '/v1/orgs/{org}/invites'),
  getInvite: op('get', '/v1/orgs/{org}/invites/{invite}'),
  resendInvite: op('post', '/v1/orgs/{org}/invites/{invite}:resend'),
  revokeInvite: op('post', '/v1/orgs/{org}/invites/{invite}:revoke'),
  getIntegrations: op('get', '/v1/orgs/{org}/integrations'),
  setGoogleSheetsIntegration: op('put', '/v1/orgs/{org}/integrations/google-sheets'),
  deleteGoogleSheetsIntegration: op('delete', '/v1/orgs/{org}/integrations/google-sheets'),
  setSmtpIntegration: op('put', '/v1/orgs/{org}/integrations/smtp'),
  deleteSmtpIntegration: op('delete', '/v1/orgs/{org}/integrations/smtp'),
  testSmtpIntegration: op('post', '/v1/orgs/{org}/integrations/smtp:test'),
  getHostSwitch: op('get', '/v1/orgs/{org}/games/{game}/host-switch'),
  publishHostSwitch: op('put', '/v1/orgs/{org}/games/{game}/host-switch'),
  deleteHostSwitch: op('delete', '/v1/orgs/{org}/games/{game}/host-switch'),
  signHostSwitch: op('post', '/v1/orgs/{org}/games/{game}/host-switch:sign'),
  signAssetUrls: op('post', '/v1/orgs/{org}/games/{game}/branches/{branch}/assets:sign'),
  banPlayer: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}:ban'),
  unbanPlayer: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}:unban'),
  // M7 slice 5 (spec/api-conventions.md section 21.5): the event stream, job cancel and retry, webhooks
  streamGameEvents: op('get', '/v1/orgs/{org}/games/{game}/events'),
  streamJobEvents: op('get', '/v1/orgs/{org}/games/{game}/jobs/{job}/events'),
  cancelJob: op('post', '/v1/orgs/{org}/games/{game}/jobs/{job}:cancel'),
  retryJob: op('post', '/v1/orgs/{org}/games/{game}/jobs/{job}:retry'),
  listWebhooks: op('get', '/v1/orgs/{org}/games/{game}/webhooks'),
  createWebhook: op('post', '/v1/orgs/{org}/games/{game}/webhooks'),
  getWebhook: op('get', '/v1/orgs/{org}/games/{game}/webhooks/{hook}'),
  updateWebhook: op('patch', '/v1/orgs/{org}/games/{game}/webhooks/{hook}'),
  deleteWebhook: op('delete', '/v1/orgs/{org}/games/{game}/webhooks/{hook}'),
  rotateWebhookSecret: op('post', '/v1/orgs/{org}/games/{game}/webhooks/{hook}:rotate-secret'),
  pingWebhook: op('post', '/v1/orgs/{org}/games/{game}/webhooks/{hook}:ping'),
  listWebhookDeliveries: op('get', '/v1/orgs/{org}/games/{game}/webhooks/{hook}/deliveries'),
  redeliverWebhookDelivery: op('post', '/v1/orgs/{org}/games/{game}/webhooks/{hook}/deliveries/{delivery}:redeliver'),
  // M7 slice 7 (spec/api-conventions.md section 21.7): the counters' reads, the per-player feed, the tester flag, the exports
  getAnalyticsDaily: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/analytics/daily'),
  getAnalyticsEntities: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/analytics/entities'),
  listPlayerEvents: op('get', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}/events'),
  updatePlayer: op('patch', '/v1/orgs/{org}/games/{game}/envs/{env}/players/{player}'),
  exportAnalytics: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/analytics:export'),
  exportPlayers: op('post', '/v1/orgs/{org}/games/{game}/envs/{env}/players:export'),
  downloadJobResult: op('get', '/v1/orgs/{org}/games/{game}/jobs/{job}/download'),
} as const;

export type OpName = keyof typeof ops;
